/**
 * GlamourGallery — Luxury Interactions JS
 * Sentinel: <!-- gg-interactions-v1 -->
 *
 * Responsibilities:
 *  1. Scroll header: add .gg-scrolled to #header-group when scrollY > 60
 *  2. Scroll reveal: IntersectionObserver on card/section elements
 *  3. Respects prefers-reduced-motion throughout
 */

(function () {
  'use strict';

  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Scroll header ───────────────────────────────────── */

  const headerGroup = document.getElementById('header-group');

  if (headerGroup) {
    let ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          if (window.scrollY > 60) {
            headerGroup.classList.add('gg-scrolled');
          } else {
            headerGroup.classList.remove('gg-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load
  }

  /* ── 2. Scroll reveal ───────────────────────────────────── */

  if (reducedMotion) return; // respect user preference — no animations

  // Elements to observe. We target:
  //   - .card-wrapper (product cards) — only inside our custom sections
  //     to avoid conflicting with Horizon's own scroll animations
  //   - Headings in our custom sections
  //   - Custom section content blocks

  const CARD_STAGGER_MS = 80;
  const HEADING_STAGGER_MS = 0;

  // Custom section selectors — elements Horizon doesn't animate
  const HEADING_SELECTORS = [
    '.rec-head',
    '.es-text',
    '.ps-head',
    '.jg-head',
    '.nl-card',
    '.ft-brand',
  ].join(', ');

  const CARD_SELECTORS = [
    '.rec-section .card-wrapper',
    '.recently-arrived .card-wrapper',
  ].join(', ');

  function applyReveal(elements, staggerMs, extraClass) {
    elements.forEach(function (el, i) {
      if (el.hasAttribute('data-gg-reveal-applied')) return;
      el.setAttribute('data-gg-reveal-applied', '');
      el.classList.add('gg-reveal');
      if (extraClass) el.classList.add(extraClass);
      el.style.setProperty('--gg-delay', (i * staggerMs) + 'ms');
    });
  }

  function observeGroup(selector, staggerMs, extraClass) {
    const elements = Array.from(document.querySelectorAll(selector));
    if (!elements.length) return;

    applyReveal(elements, staggerMs, extraClass);

    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('gg-reveal--visible');
            io.unobserve(entry.target); // only animate once
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach(function (el) { io.observe(el); });
  }

  // Wait for DOM to be fully painted before observing
  function init() {
    // Section headings — stagger 0 (each appears as one unit)
    observeGroup(HEADING_SELECTORS, HEADING_STAGGER_MS, 'gg-reveal--heading');

    // Product cards in our custom sections — staggered
    const cardGroups = document.querySelectorAll('.rec-grid, .recently-arrived__grid');
    cardGroups.forEach(function (grid) {
      const cards = Array.from(grid.querySelectorAll('.card-wrapper'));
      if (!cards.length) return;

      cards.forEach(function (card, i) {
        if (card.hasAttribute('data-gg-reveal-applied')) return;
        card.setAttribute('data-gg-reveal-applied', '');
        card.classList.add('gg-reveal');
        card.style.setProperty('--gg-delay', (i * CARD_STAGGER_MS) + 'ms');
      });

      const io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('gg-reveal--visible');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
      );

      cards.forEach(function (card) { io.observe(card); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay so Horizon finishes its own layout before we measure
    setTimeout(init, 50);
  }

  /* ── 3. Quick-view (custom branded) ─────────────────────────
     Custom luxury-quiet quick-view modal: two-column (image | info),
     fetches product JSON from /products/{handle}.js, renders our own
     markup with Playfair/Inter, walnut/brass palette, "View full
     details" link. Uses Horizon's existing <quick-add-dialog> shell
     so close button + ESC + click-outside still work.
     ATC submits to /cart/add.js and dispatches cart:update which
     opens Horizon's cart drawer (auto-open attribute).
     ─────────────────────────────────────────────────────────── */

  const QV_CACHE = new Map();           // handle -> product JSON
  let   qvInflight = null;
  let   qvStylesInjected = false;

  function esc(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function gbp(pennies) {
    if (pennies == null) return '';
    return '£' + (pennies / 100).toFixed(2).replace(/\.00$/, '');
  }
  function injectQVStyles() {
    if (qvStylesInjected) return;
    qvStylesInjected = true;
    var css = `
      /* Force Horizon's modal into our wider, branded shape when QV is active.
         We tag the dialog with [data-gg-qv] when we open it. High-specificity
         attribute selector + !important overrides Horizon's CSS variables. */
      dialog.quick-add-modal[data-gg-qv] {
        width: min(92vw, 880px) !important;
        max-width: 880px !important;
        height: max-content !important;
        max-height: 90vh !important;
        min-height: 0 !important;
        padding: 0 !important;
        background: #FFFDF7 !important;
        border: 1px solid #E8DFD0 !important;
        border-radius: 6px !important;
        overflow: hidden !important;
        display: block !important;
      }
      dialog.quick-add-modal[data-gg-qv] .quick-add-modal__close {
        top: 14px !important; right: 14px !important;
        background: rgba(255,253,247,0.86) !important;
        border: 1px solid #E8DFD0 !important;
        border-radius: 999px !important;
        width: 32px !important; height: 32px !important;
        color: #5C3D2E !important;
        z-index: 2 !important;
      }
      dialog.quick-add-modal[data-gg-qv] .quick-add-modal__content {
        display: block !important;
        grid-template-columns: none !important;
        grid-template-rows: none !important;
        width: 100% !important;
        max-width: 100% !important;
        height: max-content !important;
        min-height: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        max-height: none !important;
        flex-grow: 0 !important;
      }
      /* Belt-and-braces — if the parent's grid is still active for some reason,
         span the QV across all 7 of its columns. */
      dialog.quick-add-modal[data-gg-qv] .gg-qv {
        grid-column: 1 / -1 !important;
        grid-row: 1 / -1 !important;
        height: max-content !important;
      }
      .gg-qv {
        display: grid;
        grid-template-columns: 1fr 1fr;
        background: #FFFDF7;
        color: #5C3D2E;
        font-family: 'Inter', system-ui, sans-serif;
        align-items: start;
      }
      .gg-qv__media {
        background: #F5F0E8;
        display: flex;
        flex-direction: column;
      }
      .gg-qv__media-main {
        aspect-ratio: 1 / 1;
        overflow: hidden;
        position: relative;
      }
      .gg-qv__media-main img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
        transition: opacity 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__media-main img.is-swapping { opacity: 0.3; }
      .gg-qv__media-thumbs {
        display: flex;
        gap: 6px;
        padding: 10px;
        overflow-x: auto;
        scrollbar-width: thin;
        background: #FAF8F5;
        border-top: 1px solid #E8DFD0;
      }
      .gg-qv__thumb {
        flex: 0 0 auto;
        width: 56px; height: 56px;
        padding: 0;
        background: #FFFDF7;
        border: 1px solid #E8DFD0;
        border-radius: 3px;
        cursor: pointer;
        overflow: hidden;
        transition: border-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    box-shadow 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__thumb img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
      }
      .gg-qv__thumb:hover { border-color: rgba(201,168,124,0.55); }
      .gg-qv__thumb[data-selected="true"] {
        border-color: #C9A87C;
        border-width: 2px;
        box-shadow: 0 0 0 1px rgba(201,168,124,0.20);
      }
      .gg-qv__thumb:focus-visible {
        outline: 2px solid #C9A87C;
        outline-offset: 2px;
      }
      .gg-qv__info {
        padding: 36px 32px 32px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        overflow-y: auto;
        max-height: 90vh;
      }
      .gg-qv__eyebrow {
        font-size: 10px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: #C9A87C;
        font-weight: 600;
        margin: 0;
      }
      .gg-qv__title {
        font-family: 'Playfair Display', Georgia, serif;
        font-size: 22px;
        font-weight: 400;
        line-height: 1.25;
        color: #5C3D2E;
        margin: 0;
      }
      .gg-qv__price {
        display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        margin: 0;
        font-size: 18px;
      }
      .gg-qv__price-current { color: #5C3D2E; font-weight: 500; }
      .gg-qv__price-compare {
        color: rgba(92,61,46,0.5);
        text-decoration: line-through;
        font-size: 14px;
      }
      .gg-qv__stock {
        display: inline-flex; align-items: center; gap: 6px;
        background: rgba(138, 158, 133, 0.22);
        border: 1px solid rgba(138, 158, 133, 0.55);
        color: #3E5439;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .gg-qv__delivery {
        margin: 4px 0 0;
        font-size: 12px;
        color: rgba(92, 61, 46, 0.75);
        line-height: 1.5;
      }
      .gg-qv__delivery-label {
        font-size: 10px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #C9A87C;
        font-weight: 600;
        margin-right: 6px;
      }
      .gg-qv__delivery-range {
        color: #5C3D2E;
        font-weight: 500;
      }
      .gg-qv__stock--brass {
        background: rgba(201, 168, 124, 0.28);
        border-color: rgba(170, 120, 50, 0.55);
        color: #6B4517;
      }
      .gg-qv__stock--terracotta {
        background: rgba(183, 65, 14, 0.18);
        border-color: rgba(183, 65, 14, 0.55);
        color: #7A2A06;
      }
      .gg-qv__stock--oos {
        background: rgba(92, 61, 46, 0.08);
        border-color: rgba(92, 61, 46, 0.30);
        color: rgba(92, 61, 46, 0.65);
      }
      .gg-qv__stock-dot {
        width: 5px; height: 5px; border-radius: 50%;
        background: currentColor; opacity: 0.85;
      }
      .gg-qv__desc {
        font-size: 13px;
        line-height: 1.6;
        color: rgba(92, 61, 46, 0.78);
        margin: 4px 0 0;
      }
      .gg-qv__variants {
        display: flex; flex-wrap: wrap; gap: 8px;
        margin: 6px 0 0;
      }
      .gg-qv__variant-chip {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 7px 14px;
        border: 1px solid #E8DFD0;
        background: #FFFDF7;
        color: #5C3D2E;
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.04em;
        line-height: 1.3;
        border-radius: 999px;
        cursor: pointer;
        white-space: nowrap;
        transition: background-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    border-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    color 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__variant-chip.has-dot {
        padding-left: 8px;
      }
      .gg-qv__variant-chip:hover {
        border-color: #C9A87C;
        color: #C9A87C;
      }
      .gg-qv__variant-chip[data-selected="true"] {
        border-color: #C9A87C;
        background: #C9A87C;
        color: #FFFDF7;
      }
      .gg-qv__variant-chip:disabled {
        opacity: 0.4; cursor: not-allowed; text-decoration: line-through;
      }
      .gg-qv__variant-chip .gg-swatch-dot {
        display: inline-block;
        width: 18px; height: 18px;
        border-radius: 50%;
        border: 1px solid rgba(92,61,46,0.18);
        flex-shrink: 0;
        box-shadow: inset 0 0 0 1px rgba(255,253,247,0.4);
      }
      .gg-qv__form {
        display: flex; gap: 10px;
        margin-top: 12px;
        align-items: stretch;
      }
      .gg-qv__qty {
        display: inline-flex;
        border: 1px solid #E8DFD0;
        border-radius: 2px;
        background: transparent;
        overflow: hidden;
      }
      .gg-qv__qty button {
        width: 38px; height: 44px;
        background: transparent;
        color: #5C3D2E;
        border: none;
        font-size: 16px;
        cursor: pointer;
        transition: background-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__qty button:hover { background: rgba(201,168,124,0.16); }
      .gg-qv__qty input {
        width: 38px;
        background: transparent;
        border: none;
        border-left: 1px solid #E8DFD0;
        border-right: 1px solid #E8DFD0;
        text-align: center;
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 14px;
        color: #2B1F18;
        -moz-appearance: textfield;
      }
      .gg-qv__qty input::-webkit-outer-spin-button,
      .gg-qv__qty input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      .gg-qv__atc {
        flex: 1;
        background: #C9A87C;
        color: #FFFDF7;
        border: none;
        border-radius: 2px;
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        cursor: pointer;
        padding: 0 24px;
        transition: background-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
                    box-shadow 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__atc:hover {
        background: #B5926A;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(201, 168, 124, 0.25);
      }
      .gg-qv__atc:active { transform: translateY(0); }
      .gg-qv__atc[data-loading] { opacity: 0.7; pointer-events: none; }
      .gg-qv__atc:disabled { opacity: 0.5; cursor: not-allowed; }
      .gg-qv__details-link {
        margin-top: 8px;
        font-size: 12px;
        color: #C9A87C;
        text-decoration: none;
        align-self: flex-start;
        padding-bottom: 1px;
        border-bottom: 1px solid rgba(201,168,124,0.4);
        transition: border-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .gg-qv__details-link:hover { border-color: #C9A87C; }
      @media (max-width: 749px) {
        dialog.quick-add-modal:has(.gg-qv) { max-width: 100%; width: 100%; height: 100%; max-height: 100%; border-radius: 0; }
        .gg-qv { grid-template-columns: 1fr; }
        .gg-qv__media { aspect-ratio: 4 / 3; }
        .gg-qv__info { padding: 24px 22px 24px; max-height: none; }
      }
    `;
    var style = document.createElement('style');
    style.id = 'gg-qv-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Country-aware delivery window (matches gg-delivery-eta snippet).
  // Reflects CJ/AE typical fulfilment time to the visitor's market.
  var DELIVERY_WINDOWS = {
    'GB': [14, 28], 'US': [12, 24], 'CA': [16, 30], 'AU': [16, 30],
    'IE': [16, 30], 'NZ': [18, 32],
    'DE': [16, 30], 'FR': [16, 30], 'NL': [16, 30], 'ES': [16, 30],
    'IT': [16, 30], 'BE': [16, 30], 'SE': [16, 30], 'DK': [16, 30],
  };
  var DEFAULT_DELIVERY_WINDOW = [21, 35];
  var ETA_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var ETA_DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  function getVisitorCountry() {
    try {
      var url = new URLSearchParams(window.location.search).get('country');
      if (url) return url.toUpperCase();
    } catch (e) {}
    try {
      var stored = localStorage.getItem('gg_country');
      if (stored) return stored.toUpperCase();
    } catch (e) {}
    // Try to read Shopify's localization meta tag if present
    var meta = document.querySelector('meta[name="shopify-country"]');
    if (meta?.content) return meta.content.toUpperCase();
    return 'GB';
  }
  function fmtEtaDate(d) {
    return ETA_DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + ETA_MONTHS[d.getMonth()];
  }
  function deliveryRangeText() {
    var country = getVisitorCountry();
    var win = DELIVERY_WINDOWS[country] || DEFAULT_DELIVERY_WINDOW;
    var today = new Date();
    var from = new Date(today.getTime()); from.setDate(from.getDate() + win[0]);
    var to   = new Date(today.getTime()); to.setDate(to.getDate() + win[1]);
    return fmtEtaDate(from) + ' – ' + fmtEtaDate(to);
  }

  function stockClassFor(qty) {
    if (qty == null) return '';
    if (qty <= 0) return 'gg-qv__stock--oos';
    if (qty <= 2) return 'gg-qv__stock--terracotta';
    if (qty <= 5) return 'gg-qv__stock--brass';
    return '';   // sage (default)
  }

  function buildQVHTML(product, stockInfo, regionLocked) {
    stockInfo = stockInfo || { tier: null, text: null };
    var v = product.variants.find(function (vv) { return vv.available; }) || product.variants[0];
    var img = (product.featured_image || product.images[0] || '');
    if (img && img.indexOf('http') !== 0) img = 'https:' + img;
    var compare = (v.compare_at_price && v.compare_at_price > v.price) ? gbp(v.compare_at_price) : null;
    // Live stock — preferred source is server-rendered PDP inventory block.
    // Falls back to 'available' boolean if PDP fetch failed.
    var stockText = stockInfo.text;
    if (!stockText) {
      if (v.available === false) stockText = 'Not in stock';
      // else: leave empty (don't show pill if we have no count + product is available)
    }
    // Map PDP tier directly to our stock-pill class.
    var stockClass = '';
    if (stockInfo.tier === 'oos')         stockClass = 'gg-qv__stock--oos';
    else if (stockInfo.tier === 'red')    stockClass = 'gg-qv__stock--terracotta';
    else if (stockInfo.tier === 'yellow') stockClass = 'gg-qv__stock--brass';
    // else green tier uses default (sage) styling
    var descRaw = (product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    var desc = descRaw.length > 220 ? descRaw.slice(0, 220).trim() + '…' : descRaw;
    var hasMultipleVariants = product.variants.length > 1;
    var variantOptions = '';
    if (hasMultipleVariants) {
      var extract = (typeof window !== 'undefined' && window.ggExtractColour) ? window.ggExtractColour : null;
      variantOptions = '<div class="gg-qv__variants" role="radiogroup" aria-label="Choose variant">' +
        product.variants.map(function (vv) {
          var rawLabel = vv.title || vv.public_title || vv.option1 || ('Variant ' + vv.id);
          var info = extract ? extract(rawLabel) : { matched: false, label: rawLabel, hex: null };
          var displayLabel = info.label || rawLabel;
          var dotHTML = '';
          var hasDotClass = '';
          if (info.matched && info.hex) {
            dotHTML = '<span class="gg-swatch-dot" style="background-color:' + info.hex + ';"></span>';
            hasDotClass = ' has-dot';
          }
          var disabled = vv.available ? '' : 'disabled';
          var selected = (vv.id === v.id) ? 'true' : 'false';
          return '<button type="button" class="gg-qv__variant-chip' + hasDotClass +
                 '" data-variant-id="' + vv.id +
                 '" data-selected="' + selected + '" data-price="' + vv.price +
                 '" data-compare="' + (vv.compare_at_price || '') + '" ' + disabled +
                 ' role="radio" aria-checked="' + selected + '"' +
                 ' title="' + esc(rawLabel) + '">' +
                 dotHTML + esc(displayLabel) + '</button>';
        }).join('') + '</div>';
    }
    // Build thumbnail strip from product.images (skip if only one image)
    var images = (product.images || []).filter(Boolean).map(function (u) {
      return (u.indexOf('http') === 0) ? u : ('https:' + u);
    });
    if (!images.length && img) images = [img];
    var thumbsHTML = '';
    if (images.length > 1) {
      thumbsHTML = '<div class="gg-qv__media-thumbs" role="tablist" aria-label="Product images">' +
        images.map(function (u, i) {
          var selected = (i === 0) ? 'true' : 'false';
          return '<button type="button" class="gg-qv__thumb" data-image-url="' + esc(u) +
                 '" data-selected="' + selected + '" role="tab" aria-selected="' + selected +
                 '" aria-label="View image ' + (i + 1) + '">' +
                 '<img src="' + esc(u) + '" alt="" loading="lazy">' +
                 '</button>';
        }).join('') + '</div>';
    }

    /* Region-locked: replace stock badge, delivery, variants, and buy form
       with a quiet notice explaining why the product can't be added. The
       customer still sees images, title, price, and description — useful
       browsing for when this is back in stock. */
    var buyBlock;
    if (regionLocked) {
      buyBlock =
        '<div class="gg-qv__region-lock" role="status" aria-live="polite" style="margin:18px 0 12px;padding:16px 20px;background:#F5F0E8;border:1px solid #E8DFD0;border-left:3px solid #C9A87C;border-radius:4px;color:#5C3D2E;font-family:Inter,system-ui,sans-serif;font-size:13px;line-height:1.55;display:flex;gap:12px;align-items:flex-start;">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A87C" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;margin-top:1px;">' +
            '<rect x="3" y="11" width="18" height="11" rx="2"/>' +
            '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>' +
          '</svg>' +
          '<div>' +
            '<strong style="display:block;margin-bottom:3px;color:#5C3D2E;font-weight:600;font-size:14px;">Not available in your region</strong>' +
            '<span style="color:#6b5a4d;font-size:12.5px;">This piece can\'t currently be delivered to your country. ' +
              '<a href="/#newsletter" style="color:#C9A87C;text-decoration:none;border-bottom:1px solid rgba(201,168,124,0.4);">Subscribe to get notified</a>' +
              ' when new pieces arrive in your region.</span>' +
          '</div>' +
        '</div>';
    } else {
      buyBlock =
        '<p class="gg-qv__delivery">' +
          '<span class="gg-qv__delivery-label">Estimated delivery</span>' +
          '<span class="gg-qv__delivery-range" data-qv-delivery>' + esc(deliveryRangeText()) + '</span>' +
        '</p>' +
        variantOptions +
        '<form class="gg-qv__form" data-qv-form>' +
          '<input type="hidden" name="id" value="' + v.id + '" data-qv-variant-id>' +
          '<div class="gg-qv__qty">' +
            '<button type="button" data-qv-minus aria-label="Decrease quantity">−</button>' +
            '<input type="number" name="quantity" value="1" min="1" inputmode="numeric">' +
            '<button type="button" data-qv-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="submit" class="gg-qv__atc" data-qv-atc' + (v.available ? '' : ' disabled') + '>' +
            (v.available ? 'Add to basket' : 'Sold out') +
          '</button>' +
        '</form>';
    }

    /* Stock badge — only when NOT region-locked. Showing "10 left in stock"
       next to a "not available" notice is contradictory and confusing. */
    var stockBadgeHTML = (stockText && !regionLocked)
      ? ('<span class="gg-qv__stock ' + stockClass + '" data-qv-stock>' +
         '<span class="gg-qv__stock-dot"></span>' + esc(stockText) + '</span>')
      : '';

    return '' +
      '<div class="gg-qv"' + (regionLocked ? ' data-region-locked="true"' : '') + '>' +
        '<div class="gg-qv__media">' +
          '<div class="gg-qv__media-main">' +
            (img ? '<img src="' + esc(img) + '" alt="' + esc(product.title) + '" data-qv-main-image>' : '') +
          '</div>' +
          thumbsHTML +
        '</div>' +
        '<div class="gg-qv__info">' +
          '<p class="gg-qv__eyebrow">' + esc(product.type || 'GlamourGallery') + '</p>' +
          '<h2 class="gg-qv__title">' + esc(product.title) + '</h2>' +
          '<p class="gg-qv__price">' +
            '<span class="gg-qv__price-current" data-qv-price>' + gbp(v.price) + '</span>' +
            (compare ? '<span class="gg-qv__price-compare" data-qv-compare>' + compare + '</span>' : '<span class="gg-qv__price-compare" data-qv-compare hidden></span>') +
            stockBadgeHTML +
          '</p>' +
          (desc ? '<p class="gg-qv__desc">' + esc(desc) + '</p>' : '') +
          buyBlock +
          '<a href="/products/' + esc(product.handle) + '" class="gg-qv__details-link">View full product details →</a>' +
        '</div>' +
      '</div>';
  }

  function wireQV(container, product, dialog) {
    var form = container.querySelector('[data-qv-form]');
    var minus = container.querySelector('[data-qv-minus]');
    var plus  = container.querySelector('[data-qv-plus]');
    var input = container.querySelector('input[name="quantity"]');
    var variantInput = container.querySelector('[data-qv-variant-id]');
    var priceEl = container.querySelector('[data-qv-price]');
    var compareEl = container.querySelector('[data-qv-compare]');
    var atc   = container.querySelector('[data-qv-atc]');

    minus?.addEventListener('click', function () {
      var n = parseInt(input.value, 10) || 1;
      if (n > 1) input.value = n - 1;
    });
    plus?.addEventListener('click', function () {
      var n = parseInt(input.value, 10) || 1;
      input.value = n + 1;
    });

    // Thumbnail click handler — swap main image with a soft fade
    var mainImage = container.querySelector('[data-qv-main-image]');
    container.querySelectorAll('.gg-qv__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var url = thumb.dataset.imageUrl;
        if (!url || !mainImage) return;
        container.querySelectorAll('.gg-qv__thumb').forEach(function (t) {
          t.dataset.selected = 'false';
          t.setAttribute('aria-selected', 'false');
        });
        thumb.dataset.selected = 'true';
        thumb.setAttribute('aria-selected', 'true');
        // Fade out, swap, fade in
        mainImage.classList.add('is-swapping');
        var pre = new Image();
        pre.onload = function () {
          mainImage.src = url;
          requestAnimationFrame(function () { mainImage.classList.remove('is-swapping'); });
        };
        pre.src = url;
      });
    });

    container.querySelectorAll('.gg-qv__variant-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.disabled) return;
        container.querySelectorAll('.gg-qv__variant-chip').forEach(function (c) {
          c.dataset.selected = 'false';
          c.setAttribute('aria-checked', 'false');
        });
        chip.dataset.selected = 'true';
        chip.setAttribute('aria-checked', 'true');
        variantInput.value = chip.dataset.variantId;
        if (priceEl)   priceEl.textContent = gbp(parseInt(chip.dataset.price, 10));
        if (compareEl) {
          var c = parseInt(chip.dataset.compare, 10);
          if (c && c > parseInt(chip.dataset.price, 10)) {
            compareEl.textContent = gbp(c);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      });
    });

    form?.addEventListener('submit', async function (e) {
      e.preventDefault();
      atc.setAttribute('data-loading', '');
      atc.textContent = 'Adding…';
      try {
        // Horizon morphs cart drawer from server-rendered section HTML — we
        // must request `sections` in the POST and pass them in detail.data
        // when dispatching cart:update. Otherwise drawer opens with stale state.
        var sectionIds = [];
        document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
          sectionIds.push(el.dataset.sectionId);
        });
        var fd = new FormData(form);
        var idVal = fd.get('id');
        var qtyVal = parseInt(fd.get('quantity') || '1', 10) || 1;
        var r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items:    [{ id: Number(idVal), quantity: qtyVal }],
            sections: sectionIds.join(','),
          }),
        });
        if (!r.ok) {
          var err = await r.json().catch(function () { return null; });
          throw new Error((err && err.description) || ('cart/add ' + r.status));
        }
        var response = await r.json();
        var cart = await fetch('/cart.js').then(function (cr) { return cr.json(); }).catch(function () { return null; });
        // Close the quick view first
        if (dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
        else if (dialog && dialog.refs && dialog.refs.dialog) dialog.refs.dialog.close();
        // Dispatch the cart:update event WITH the rendered section HTML so
        // Horizon's cart-drawer-component morphs itself before opening.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'gg-quick-view',
            data:     { sections: response.sections, source: 'gg-quick-view' },
          },
        }));
        // Open via the cart-icon trigger (Horizon's normal flow).
        var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]')
                   || document.querySelector('cart-drawer-component [on\\:click="/open"]');
        if (trigger) setTimeout(function () { trigger.click(); }, 80);
      } catch (err) {
        atc.removeAttribute('data-loading');
        atc.textContent = 'Add to basket';
        alert('Could not add to basket: ' + (err.message || err));
      }
    });
  }

  async function ggQuickView(handle, btn) {
    if (!handle) return;
    var url = '/products/' + handle;
    var modalContent = document.getElementById('quick-add-modal-content');
    var dialog       = document.getElementById('quick-add-dialog');
    if (!modalContent || !dialog) { window.location.href = url; return; }

    if (btn) { btn.setAttribute('data-loading', ''); btn.setAttribute('aria-busy', 'true'); }
    injectQVStyles();

    try {
      var cached = QV_CACHE.get(handle);
      var product, stockInfo;
      if (cached) {
        product = cached.product;
        stockInfo = cached.stockInfo;
      } else {
        if (qvInflight) qvInflight.abort();
        qvInflight = new AbortController();
        // Fetch product JSON + PDP HTML in parallel — PDP gives us the
        // server-rendered stock count which /products/{handle}.js omits.
        var [jsonRes, pdpRes] = await Promise.all([
          fetch('/products/' + handle + '.js', { signal: qvInflight.signal }),
          fetch('/products/' + handle,         { signal: qvInflight.signal }),
        ]);
        if (!jsonRes.ok) throw new Error('fetch ' + jsonRes.status);
        product = await jsonRes.json();
        stockInfo = { tier: null, text: null };
        if (pdpRes.ok) {
          var pdpHtml = await pdpRes.text();
          var tierM = pdpHtml.match(/data-tier="(green|yellow|red|oos)"/);
          var textM = pdpHtml.match(/class="product-inventory__text"[^>]*>([^<]+)</);
          if (tierM) stockInfo.tier = tierM[1];
          if (textM) stockInfo.text = textM[1].trim();
        }
        QV_CACHE.set(handle, { product: product, stockInfo: stockInfo });
      }

      /* Region-lock detection — check the source element AND its
         ancestors for data-ships-visitor="false". Also accept the case
         where btn itself is the card (anchor). */
      var regionLocked = false;
      var src = btn;
      if (src) {
        // Element itself
        if (src.getAttribute && src.getAttribute('data-ships-visitor') === 'false') {
          regionLocked = true;
        }
        // Or a parent
        if (!regionLocked && typeof src.closest === 'function') {
          var card = src.closest('[data-ships-visitor]');
          if (card && card.getAttribute('data-ships-visitor') === 'false') {
            regionLocked = true;
          }
        }
      }
      // Fallback — look up by handle in case btn was null
      if (!regionLocked) {
        var cardByHandle = document.querySelector('.rec-card[href*="/products/' + handle + '"][data-ships-visitor="false"]');
        if (cardByHandle) regionLocked = true;
      }
      modalContent.innerHTML = buildQVHTML(product, stockInfo, regionLocked);
      wireQV(modalContent, product, dialog);

      // Tag the inner <dialog> so our high-specificity CSS overrides apply.
      var innerDialog = dialog.querySelector('dialog.quick-add-modal') || dialog.querySelector('dialog');
      if (innerDialog) {
        innerDialog.setAttribute('data-gg-qv', '');
        // Clean up the tag when the modal closes (so Horizon's own
        // quick-add still works normally if it's used elsewhere).
        var clearTag = function () {
          innerDialog.removeAttribute('data-gg-qv');
          innerDialog.removeEventListener('close', clearTag);
        };
        innerDialog.addEventListener('close', clearTag);
      }

      if (typeof dialog.showDialog === 'function') dialog.showDialog();
      else if (typeof dialog.showModal === 'function') dialog.showModal();
      else if (innerDialog && typeof innerDialog.showModal === 'function') innerDialog.showModal();
    } catch (err) {
      if (err.name !== 'AbortError') window.location.href = url;
    } finally {
      if (btn) { btn.removeAttribute('data-loading'); btn.removeAttribute('aria-busy'); }
    }
  }

  // Event delegation — works for filtered/paginated cards too. We accept
  // either [data-quickview-trigger] (legacy) or [data-quick-view] (used by
  // the recently-arrived card snippet — value IS the product handle).
  document.addEventListener('click', function (e) {
    // Form 1: data-quickview-trigger with separate data-product-handle
    var btn = e.target.closest('[data-quickview-trigger]');
    if (btn) {
      e.preventDefault();
      var handle = btn.dataset.productHandle
                || btn.closest('[data-product-handle]')?.dataset.productHandle;
      ggQuickView(handle, btn);
      return;
    }
    // Form 2: data-quick-view="<handle>" (the card snippet shape)
    var btn2 = e.target.closest('[data-quick-view]');
    if (btn2) {
      e.preventDefault();
      e.stopPropagation();      // stop the card's <a href> from navigating
      var handle2 = btn2.getAttribute('data-quick-view');
      ggQuickView(handle2, btn2);
      return;
    }
  });

  // Expose API + event listener for callers that prefer it.
  // When called without a button, try to find the matching card so we can
  // still read region-lock state from data-ships-visitor.
  function findCardByHandle(handle) {
    if (!handle) return null;
    // Card itself: <a href="/products/<handle>" data-ships-visitor=...>
    var card = document.querySelector('.rec-card[href*="/products/' + handle + '"]');
    if (card) return card;
    // Or the trigger button (the section's own handler points at this)
    var trig = document.querySelector('[data-quick-view="' + handle + '"]');
    return trig;
  }
  window.GG = window.GG || {};
  window.GG.quickView = window.GG.quickView || {
    open: function (handle) {
      var src = findCardByHandle(handle);
      ggQuickView(handle, src);
    }
  };
  document.addEventListener('gg:quickview:open', function (e) {
    var h = e.detail && e.detail.handle;
    if (h) ggQuickView(h, findCardByHandle(h));
  });

})();

/* ============================================================
   GG Wishlist — bundled here so it loads globally without
   touching theme.liquid. Mirrors assets/wishlist.js.
   Selectors: .rec-card__fav, .gg-card__heart, .gg-favourite,
              [data-wishlist-toggle], [data-fav-btn]
   Handle attrs: data-product-handle | data-wishlist-toggle | data-handle
   Storage: localStorage['gg_wishlist'] = JSON array of handles
   ============================================================ */
(function () {
  'use strict';
  if (window.GG && window.GG.wishlist) return; // already bundled / already loaded

  var STORAGE_KEY = 'gg_wishlist';

  function load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (e) { return []; }
  }
  function save(handles) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(handles)); }
    catch (e) { /* storage unavailable */ }
  }
  function isSaved(handle) { return load().indexOf(String(handle)) !== -1; }
  function toggle(handle) {
    var h   = String(handle);
    var arr = load();
    var i   = arr.indexOf(h);
    var added;
    if (i === -1) { arr.push(h); added = true; }
    else          { arr.splice(i, 1); added = false; }
    save(arr);
    document.dispatchEvent(new CustomEvent('gg:wishlist:change', {
      detail: { handle: h, saved: added, all: arr }
    }));
    return added;
  }

  window.GG = window.GG || {};
  window.GG.wishlist = {
    getAll: load,
    isSaved: isSaved,
    toggle: toggle,
    count: function () { return load().length; }
  };

  /* Inject a count badge into the header wishlist link if not already present.
     Header markup is do-not-touch, so badge + SVG wrapper are created at runtime.
     The wrapper mirrors cart-icon's role: it hugs the SVG so the badge can be
     positioned with the same top/right offsets as Horizon's cart-bubble. */
  function ensureHeaderBadge() {
    var link = document.querySelector('a.header-actions__action--wishlist');
    if (!link) return;
    if (link.querySelector('[data-wishlist-count]')) return;
    var svg = link.querySelector('svg.gg-heart');
    if (svg && !svg.parentElement.classList.contains('gg-wishlist-icon-wrap')) {
      var wrap = document.createElement('span');
      wrap.className = 'gg-wishlist-icon-wrap';
      svg.parentNode.insertBefore(wrap, svg);
      wrap.appendChild(svg);
    }
    var host = link.querySelector('.gg-wishlist-icon-wrap') || link;
    var span = document.createElement('span');
    span.className = 'gg-wishlist-count';
    span.setAttribute('data-wishlist-count', '');
    span.setAttribute('aria-hidden', 'true');
    span.style.display = 'none';
    host.appendChild(span);
  }

  function updateBadge() {
    ensureHeaderBadge();
    var n = load().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n > 0 ? n : '';
      el.style.display = n > 0 ? '' : 'none';
    });
  }

  var SEL = '.rec-card__fav, .gg-card__heart, .gg-favourite, [data-wishlist-toggle], [data-fav-btn]';

  function readHandle(btn) {
    return btn.getAttribute('data-product-handle')
        || btn.getAttribute('data-wishlist-toggle')
        || btn.getAttribute('data-handle');
  }

  function syncButtons() {
    var saved = load();
    document.querySelectorAll(SEL).forEach(function (btn) {
      var handle = readHandle(btn);
      if (!handle) return;
      var on = saved.indexOf(handle) !== -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    });
  }

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var btn = e.target.closest(SEL);
    if (!btn) return;
    var handle = readHandle(btn);
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    var added = toggle(handle);
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    btn.classList.toggle('is-saved', added);
    btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Save to wishlist');
    updateBadge();
  }, true);

  function imageWithWidth(src, width) {
    if (!src) return '';
    var clean = String(src).replace(/[?&]width=\d+/, '');
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }
  function money(cents) {
    if (cents == null) return '';
    return '£' + (cents / 100).toFixed(2);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function renderCard(p) {
    var url = p.url || '/products/' + p.handle;
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var imgUrl = img ? imageWithWidth(img, 720) : '';
    var srcset = img ? [
      imageWithWidth(img, 360) + ' 360w',
      imageWithWidth(img, 540) + ' 540w',
      imageWithWidth(img, 720) + ' 720w',
      imageWithWidth(img, 1080) + ' 1080w'
    ].join(', ') : '';
    var compareAt = (p.compare_at_price && p.compare_at_price > p.price) ? p.compare_at_price : null;
    var subtitle  = p.type || '';
    return (
      '<a href="' + esc(url) + '" class="rec-card" aria-label="' + esc(p.title) + '">' +
        '<div class="rec-card__image">' +
          (imgUrl
            ? '<img class="rec-card__img" src="' + esc(imgUrl) + '" srcset="' + esc(srcset) + '" sizes="(min-width: 1080px) 22vw, (min-width: 760px) 30vw, 45vw" alt="' + esc(p.title) + '" loading="lazy" width="720" height="900">'
            : '<div class="rec-card__img rec-card__img--placeholder"></div>') +
          '<button class="rec-card__fav is-saved" type="button" aria-pressed="true" aria-label="Remove from wishlist" data-product-handle="' + esc(p.handle) + '">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/></svg>' +
          '</button>' +
        '</div>' +
        '<h3 class="rec-card__name">' + esc(p.title) + '</h3>' +
        (subtitle ? '<p class="rec-card__meta">' + esc(subtitle) + '</p>' : '') +
        '<div class="rec-card__price-row">' +
          '<span class="rec-card__price">' + money(p.price) + '</span>' +
          (compareAt ? '<span class="rec-card__compare">' + money(compareAt) + '</span>' : '') +
        '</div>' +
      '</a>'
    );
  }

  function renderWishlistPage() {
    var grid  = document.querySelector('[data-wishlist-grid]');
    var empty = document.querySelector('[data-wishlist-empty]');
    if (!grid) return;
    var list = load();
    if (list.length === 0) {
      grid.innerHTML = '';
      if (empty) empty.removeAttribute('hidden');
      return;
    }
    if (empty) empty.setAttribute('hidden', '');
    grid.setAttribute('aria-busy', 'true');
    Promise.all(list.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      grid.innerHTML = products
        .filter(function (p) { return p && p.handle; })
        .map(renderCard)
        .join('');
      grid.removeAttribute('aria-busy');
      syncButtons();
    });
  }

  function init() {
    updateBadge();
    syncButtons();
    renderWishlistPage();
    document.dispatchEvent(new CustomEvent('gg:wishlist:ready', { detail: { saved: load() } }));
  }
  document.addEventListener('gg:wishlist:change', function () {
    syncButtons();
    updateBadge();
    renderWishlistPage();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}());

/* ============================================================
   GG Recently Viewed — cross-page localStorage strip.
   On every PDP visit, push the handle to localStorage and (if
   we're on a PDP) render a strip of the last 4 viewed pieces
   below the product details. Same data the search modal uses,
   surfaced where customers care most about discovery.
   ============================================================ */
(function () {
  'use strict';
  if (window.GG && window.GG.recentlyViewed) return;
  var KEY = 'gg_recently_viewed';
  var MAX = 12;

  function load() {
    try {
      var arr = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(arr)) return [];
      // Normalise legacy entries (strings) to objects { h, l }
      return arr.map(function (e) {
        if (typeof e === 'string') return { h: e, l: false };
        return e;
      }).filter(function (e) { return e && e.h; });
    }
    catch (e) { return []; }
  }
  function save(entries) {
    try { localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX))); }
    catch (e) { /* ignore */ }
  }

  function currentProductHandle() {
    var m = location.pathname.match(/^\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* Detect whether the CURRENT page (PDP) is region-locked for the visitor.
     Reads the same flag the PDP renders, so the strip knows without an
     extra metafield fetch. */
  function currentPageIsRegionLocked() {
    var notice = document.querySelector('.gg-region-lock[data-region-locked="true"]');
    return !!notice;
  }

  function record(handle, locked) {
    if (!handle) return;
    var arr = load();
    var idx = -1;
    for (var i = 0; i < arr.length; i++) { if (arr[i].h === handle) { idx = i; break; } }
    if (idx !== -1) arr.splice(idx, 1);
    arr.unshift({ h: handle, l: !!locked });
    save(arr);
  }

  window.GG = window.GG || {};
  window.GG.recentlyViewed = { load: load, record: record };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function imageWithWidth(src, w) {
    if (!src) return '';
    var clean = String(src).replace(/[?&]width=\d+/, '');
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  function money(cents) {
    if (cents == null) return '';
    return '£' + (cents / 100).toFixed(2);
  }

  function renderCard(p, locked) {
    var url = p.url || '/products/' + p.handle;
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var cls = 'gg-rv-card' + (locked ? ' gg-rv-card--locked' : '');
    var lockBadge = locked
      ? ('<span class="gg-rv-card__lock" aria-hidden="true">' +
           '<svg viewBox="0 0 16 16" width="10" height="10">' +
             '<rect x="3" y="7" width="10" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
             '<path d="M5 7V5a3 3 0 016 0v2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
           '</svg>' +
           '<span>Not in your region</span>' +
         '</span>')
      : '';
    return (
      '<a href="' + esc(url) + '" class="' + cls + '" aria-label="' + esc(p.title) + '">' +
        '<span class="gg-rv-card__image-wrap">' +
          (img
            ? '<img class="gg-rv-card__img" src="' + esc(imageWithWidth(img, 360)) + '" alt="' + esc(p.title) + '" loading="lazy" width="180" height="225">'
            : '<span class="gg-rv-card__img gg-rv-card__img--placeholder"></span>') +
          lockBadge +
        '</span>' +
        '<span class="gg-rv-card__name">' + esc(p.title) + '</span>' +
        '<span class="gg-rv-card__price">' + money(p.price) + '</span>' +
      '</a>'
    );
  }

  // Find the right anchor for the strip: the section after product-information.
  // Falls back to <main>'s last child.
  function findAnchor() {
    var pi = document.querySelector('section.product-information, .product-information, [class*="product-information"]');
    if (pi && pi.parentElement) return pi;
    var main = document.querySelector('main') || document.body;
    return main.lastElementChild || main;
  }

  /* Region-lock cache keyed by product handle (visitor-specific in practice
     since lock state depends on the visitor's country, but we accept the
     edge case where a visitor changes country and the cache is briefly
     stale — cleared on next PDP visit). */
  var LOCK_CACHE_KEY = 'gg_region_lock_cache';
  function loadLockCache() {
    try { var c = JSON.parse(localStorage.getItem(LOCK_CACHE_KEY) || '{}'); return c && typeof c === 'object' ? c : {}; }
    catch (e) { return {}; }
  }
  function saveLockCache(cache) {
    try { localStorage.setItem(LOCK_CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }
  function setLockForHandle(handle, locked) {
    if (!handle) return;
    var c = loadLockCache();
    c[handle] = !!locked;
    saveLockCache(c);
  }

  /* Fetch the PDP HTML and look for the region-lock element. Returns a
     promise resolving to a boolean. */
  function fetchLockForHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = html.match(/class="[^"]*gg-region-lock[^"]*"[^>]*data-region-locked="true"/);
        var locked = !!m;
        setLockForHandle(handle, locked);
        return locked;
      })
      .catch(function () { return false; });
  }

  function renderStrip(entries, current) {
    var hostId = 'gg-rv-strip';
    if (document.getElementById(hostId)) return;
    /* Pool = full history minus current product. We resolve lock status for
       each, FILTER OUT the locked ones, then take the first 4 unlocked.
       This keeps the strip useful — only pieces the visitor can actually buy. */
    var pool = entries.filter(function (e) { return e.h !== current; });
    if (!pool.length) return;

    var host = document.createElement('section');
    host.id = hostId;
    host.className = 'gg-rv';
    host.innerHTML =
      '<div class="gg-rv__inner">' +
        '<span class="gg-rv__eyebrow">Pieces you’ve looked at</span>' +
        '<h2 class="gg-rv__heading">Picked up where you left off.</h2>' +
        '<div class="gg-rv__grid" data-rv-grid aria-busy="true"></div>' +
      '</div>';
    var anchor = findAnchor();
    anchor.parentNode.insertBefore(host, anchor.nextSibling);
    var grid = host.querySelector('[data-rv-grid]');
    var cache = loadLockCache();

    function resolveLock(e) {
      if (typeof e.l === 'boolean' && e.l === true) return Promise.resolve(true);
      if (cache.hasOwnProperty(e.h)) return Promise.resolve(!!cache[e.h]);
      return fetchLockForHandle(e.h);
    }

    /* First: resolve lock for everyone in the pool */
    Promise.all(pool.map(function (e) {
      return resolveLock(e).then(function (locked) {
        return { entry: e, locked: locked };
      });
    })).then(function (resolved) {
      /* Keep only unlocked (shippable to visitor) */
      var unlocked = resolved.filter(function (r) { return !r.locked; }).slice(0, 4);
      if (!unlocked.length) {
        /* Nothing shippable in their history — hide the strip entirely */
        host.remove();
        return;
      }
      /* Now fetch product JSON for those 4 and render */
      return Promise.all(unlocked.map(function (r) {
        return fetch('/products/' + encodeURIComponent(r.entry.h) + '.js', { credentials: 'same-origin' })
          .then(function (rr) { return rr.ok ? rr.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        var html = products.filter(function (p) { return p && p.handle; })
                          .map(function (p) { return renderCard(p, false); })
                          .join('');
        if (!html) { host.remove(); return; }
        grid.innerHTML = html;
        grid.removeAttribute('aria-busy');
      });
    });
  }

  function init() {
    var current = currentProductHandle();
    if (current) {
      /* Capture the CURRENT page's region-lock status so the strip can
         render the badge later on other pages without an extra fetch. */
      record(current, currentPageIsRegionLocked());
      renderStrip(load(), current);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}());


/* ============================================================ */
/* === GG PDP LIGHTBOX — INJECT OUR OWN CLOSE BUTTON ========== */
/* ============================================================ */
/* After five rounds of trying to restyle Horizon's close button via
   CSS specificity, CSS variables, and inline styles, we go surgical:
   hide Horizon's button (via CSS), inject our own pristine brand
   button when the dialog opens. New element = zero cascade interference. */
(function () {
  if (typeof window === 'undefined') return;
  function closeDialog(dialog) {
    var zd = dialog.closest('zoom-dialog');
    if (zd && typeof zd.close === 'function') {
      try { zd.close(); return; } catch (e) {}
    }
    if (typeof dialog.close === 'function') {
      try { dialog.close(); return; } catch (e) {}
    }
    dialog.removeAttribute('open');
  }
  function injectCloseButton(dialog) {
    if (dialog.querySelector('.gg-zoom-close')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gg-zoom-close';
    btn.setAttribute('aria-label', 'Close');
    btn.style.cssText =
      'position:absolute;top:20px;right:20px;width:44px;height:44px;' +
      'display:inline-flex;align-items:center;justify-content:center;' +
      'border-radius:50%;background:#FFFDF7;color:#5C3D2E;' +
      'border:1px solid #E8DFD0;box-shadow:0 2px 8px rgba(92,61,46,0.10);' +
      'cursor:pointer;z-index:1000;padding:0;margin:0;' +
      'transition:background-color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
        'color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
        'border-color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
        'transform 240ms cubic-bezier(0.22,0.61,0.36,1);';
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '<path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>';
    btn.addEventListener('mouseenter', function () {
      btn.style.color = '#C9A87C'; btn.style.borderColor = '#C9A87C';
      btn.style.transform = 'translateY(-1px)';
    });
    btn.addEventListener('mouseleave', function () {
      btn.style.color = '#5C3D2E'; btn.style.borderColor = '#E8DFD0';
      btn.style.transform = 'translateY(0)';
    });
    btn.addEventListener('click', function () { closeDialog(dialog); });
    dialog.appendChild(btn);
  }
  /* Prev/next chevrons — quiet brass arrows, hover-revealed on desktop. */
  function injectChevrons(dialog) {
    if (dialog.querySelector('.gg-zoom-nav')) return;
    function makeArrow(side) {
      var el = document.createElement('button');
      el.type = 'button';
      el.className = 'gg-zoom-nav gg-zoom-nav--' + side;
      el.setAttribute('aria-label', side === 'prev' ? 'Previous image' : 'Next image');
      el.style.cssText =
        'position:absolute;top:50%;' + side + ':24px;transform:translateY(-50%);' +
        'width:48px;height:48px;display:none;align-items:center;justify-content:center;' +
        'border-radius:50%;background:rgba(255,253,247,0.92);color:#5C3D2E;' +
        'border:1px solid #E8DFD0;box-shadow:0 2px 12px rgba(92,61,46,0.10);' +
        'cursor:pointer;z-index:1000;padding:0;margin:0;opacity:0;' +
        'transition:opacity 240ms cubic-bezier(0.22,0.61,0.36,1),' +
          'background-color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
          'color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
          'border-color 240ms cubic-bezier(0.22,0.61,0.36,1),' +
          'transform 240ms cubic-bezier(0.22,0.61,0.36,1);';
      var d = side === 'prev' ? 'M11 3L5 9l6 6' : 'M5 3l6 6-6 6';
      el.innerHTML =
        '<svg width="14" height="18" viewBox="0 0 16 18" fill="none" aria-hidden="true">' +
          '<path d="' + d + '" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      el.addEventListener('mouseenter', function () {
        el.style.color = '#C9A87C'; el.style.borderColor = '#C9A87C';
        el.style.background = '#FFFDF7';
        el.style.transform = 'translateY(-50%) scale(1.05)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.color = '#5C3D2E'; el.style.borderColor = '#E8DFD0';
        el.style.background = 'rgba(255,253,247,0.92)';
        el.style.transform = 'translateY(-50%) scale(1)';
      });
      el.addEventListener('click', function () { navigate(dialog, side === 'prev' ? -1 : 1); });
      return el;
    }
    var prev = makeArrow('prev');
    var next = makeArrow('next');
    dialog.appendChild(prev);
    dialog.appendChild(next);
    /* Desktop only — show on dialog hover */
    function isDesktop() { return window.matchMedia('(min-width: 750px) and (hover: hover)').matches; }
    function showArrows() { if (!isDesktop()) return; prev.style.display = 'inline-flex'; next.style.display = 'inline-flex'; prev.style.opacity = '1'; next.style.opacity = '1'; }
    function hideArrows() { prev.style.opacity = '0'; next.style.opacity = '0'; }
    dialog.addEventListener('mouseenter', showArrows);
    dialog.addEventListener('mouseleave', hideArrows);
    /* Show arrows on dialog open too (so they're discoverable before mouse-over) */
    showArrows();
    setTimeout(function () { if (!dialog.matches(':hover')) hideArrows(); }, 1800);
  }
  function navigate(dialog, delta) {
    var gallery = dialog.querySelector('.dialog-zoomed-gallery');
    if (!gallery) return;
    var items = gallery.querySelectorAll('li');
    if (!items.length) return;
    /* Find currently centered item via scrollLeft */
    var scrollLeft = gallery.scrollLeft;
    var itemWidth = items[0].getBoundingClientRect().width;
    var currentIdx = Math.round(scrollLeft / itemWidth);
    var nextIdx = Math.max(0, Math.min(items.length - 1, currentIdx + delta));
    items[nextIdx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }
  /* Backdrop click — close when user clicks the cream space around the image. */
  function bindBackdropClose(dialog) {
    if (dialog.dataset.ggBackdropBound === '1') return;
    dialog.dataset.ggBackdropBound = '1';
    dialog.addEventListener('click', function (e) {
      /* Ignore clicks on interactive children */
      if (e.target.closest('.gg-zoom-close, .gg-zoom-nav, .dialog-thumbnails-list, .dialog-thumbnails-list-container, img, video, button, a, [role="button"], drag-zoom-wrapper')) return;
      closeDialog(dialog);
    });
  }
  function injectAll() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.dialog-zoomed-gallery__dialog'),
      function (d) { injectCloseButton(d); injectChevrons(d); bindBackdropClose(d); }
    );
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectAll);
  } else {
    injectAll();
  }
  // Also catch zoom dialogs added later (e.g. variant swap rebuilds the media-gallery)
  var obs = new MutationObserver(injectAll);
  obs.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================ */
/* === GG PDP LIGHTBOX — PRELOAD (defer to Horizon transition) === */
/* ============================================================ */
/* Horizon's zoom-dialog.js already runs a proper View Transitions API
   morph between the clicked thumbnail and the dialog image (assets/zoom-
   dialog.js lines 45-83). Our previous FLIP overlay was competing with it
   and making the open feel laggy. We've removed the clone; all we keep
   is the eager-preload so the high-res image is cached before the user
   clicks. The CSS view-transition rules in glamourgallery-interactions.css
   tune the timing to brand spec. */
(function () {
  if (typeof window === 'undefined') return;

  /* Disabled — was competing with Horizon's native view transition.
     Keeping the function definition in case we need to fall back. */
  function flipOpenDisabled(srcImg) {
    var rect = srcImg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    var src = srcImg.currentSrc || srcImg.src;
    if (!src) return;

    // Ivory overlay that fades in to mask the page underneath
    var overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:9998;' +
      'background:#FFFDF7;pointer-events:none;opacity:0;' +
      'transition:opacity 320ms cubic-bezier(0.22,0.61,0.36,1);';

    // Clone of the clicked image, positioned exactly over the source
    var clone = document.createElement('img');
    clone.src = src;
    clone.alt = '';
    clone.style.cssText =
      'position:fixed;' +
      'top:' + rect.top + 'px;' +
      'left:' + rect.left + 'px;' +
      'width:' + rect.width + 'px;' +
      'height:' + rect.height + 'px;' +
      'object-fit:cover;' +
      'z-index:9999;pointer-events:none;margin:0;padding:0;' +
      'border-radius:2px;' +
      'box-shadow:0 8px 32px rgba(92,61,46,0);' +
      'transition:top 520ms cubic-bezier(0.22,0.61,0.36,1),' +
        'left 520ms cubic-bezier(0.22,0.61,0.36,1),' +
        'width 520ms cubic-bezier(0.22,0.61,0.36,1),' +
        'height 520ms cubic-bezier(0.22,0.61,0.36,1),' +
        'box-shadow 520ms cubic-bezier(0.22,0.61,0.36,1);';

    document.body.appendChild(overlay);
    document.body.appendChild(clone);

    // Compute target box — should match what the lightbox image will occupy
    requestAnimationFrame(function () {
      overlay.style.opacity = '1';

      var maxW = Math.min(window.innerWidth * 0.82, 1100);
      var maxH = Math.min(window.innerHeight * 0.72, 780);
      var ar = rect.width / Math.max(rect.height, 1);
      var targetW, targetH;
      if (maxW / maxH > ar) {
        targetH = maxH;
        targetW = maxH * ar;
      } else {
        targetW = maxW;
        targetH = maxW / ar;
      }
      var targetTop = (window.innerHeight - targetH) / 2;
      var targetLeft = (window.innerWidth - targetW) / 2;

      clone.style.top = targetTop + 'px';
      clone.style.left = targetLeft + 'px';
      clone.style.width = targetW + 'px';
      clone.style.height = targetH + 'px';
      clone.style.boxShadow = '0 8px 32px rgba(92,61,46,0.08)';
    });

    // Fade out after the move animation completes — by then the dialog
    // (which has been opening behind the overlay) is fully rendered.
    setTimeout(function () {
      overlay.style.transition = 'opacity 240ms cubic-bezier(0.22,0.61,0.36,1)';
      clone.style.transition  = 'opacity 240ms cubic-bezier(0.22,0.61,0.36,1)';
      overlay.style.opacity = '0';
      clone.style.opacity   = '0';
      setTimeout(function () {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        if (clone.parentNode)   clone.parentNode.removeChild(clone);
      }, 260);
    }, 540);
  }

  // (Capture-phase FLIP click handler removed — was competing with Horizon's
  //  native view transition. flipOpenDisabled() is kept above only as a
  //  reference / fallback for older browsers without view transitions.)

  // Preload dialog images via <link rel="preload" as="image">. The dialog
  // is display:none until opened, so browsers skip Image() probes on
  // images in hidden containers. <link rel="preload"> bypasses that —
  // it sits in <head> and forces an immediate, high-priority fetch.
  var _ggPreloadedUrls = {};
  function preloadDialogImages() {
    var head = document.head;
    var urls = [];
    function addUrl(url) {
      if (!url) return;
      url = String(url).trim();
      if (!url || _ggPreloadedUrls[url]) return;
      _ggPreloadedUrls[url] = 1;
      urls.push(url);
    }
    // 1. <img> elements in the dialog
    var imgs = document.querySelectorAll('.dialog-zoomed-gallery li img, .dialog-zoomed-gallery li picture img');
    Array.prototype.forEach.call(imgs, function (img) {
      addUrl(img.getAttribute('src'));
      addUrl(img.getAttribute('data_max_resolution'));
      var srcset = img.getAttribute('srcset') || '';
      srcset.split(',').forEach(function (entry) {
        addUrl(entry.trim().split(/\s+/)[0]);
      });
    });
    // 2. <source> elements in the dialog
    var sources = document.querySelectorAll('.dialog-zoomed-gallery li source, .dialog-zoomed-gallery li picture source');
    Array.prototype.forEach.call(sources, function (src) {
      var srcset = src.getAttribute('srcset') || '';
      srcset.split(',').forEach(function (entry) {
        addUrl(entry.trim().split(/\s+/)[0]);
      });
    });
    // 3. Inject <link rel="preload" as="image"> for each unique URL
    urls.forEach(function (url) {
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      link.setAttribute('fetchpriority', 'high');
      head.appendChild(link);
    });
    // 4. Belt-and-braces: also flip loading=lazy → eager on the dialog imgs
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.loading === 'lazy') img.loading = 'eager';
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
    // 5. CRITICAL: pre-DECODE each image. Even cached images need decoding
    //    into raster before they can paint — that's the "blank for a beat"
    //    the user keeps seeing. Image.decode() returns a promise that
    //    resolves only when the image is fully ready to paint with no delay.
    urls.forEach(function (url) {
      var probe = new Image();
      probe.src = url;
      if (typeof probe.decode === 'function') {
        probe.decode().catch(function () { /* decode can fail on data: URIs etc — ignore */ });
      }
    });
  }
  /* Dialog priming — briefly open the zoom-dialog invisibly at page idle
     so the browser decodes all images and lays out the dialog DOM. After
     this, subsequent opens are effectively instant because everything is
     already rendered + decoded in the GPU's texture cache. */
  function primeDialog() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.dialog-zoomed-gallery__dialog'),
      function (dialog) {
        if (dialog.dataset.ggPrimed === '1') return;
        if (dialog.hasAttribute('open')) return; /* already open — don't disturb */
        dialog.dataset.ggPrimed = '1';
        /* Move off-screen and make invisible BEFORE opening so the user
           never sees it. Then call show() (non-modal) which lays out and
           paints the dialog without locking the page. Close after 200ms. */
        var origStyle = dialog.getAttribute('style') || '';
        dialog.style.setProperty('position', 'fixed', 'important');
        dialog.style.setProperty('inset', 'auto auto -200vh -200vw', 'important');
        dialog.style.setProperty('opacity', '0', 'important');
        dialog.style.setProperty('pointer-events', 'none', 'important');
        dialog.style.setProperty('visibility', 'hidden', 'important');
        try {
          if (typeof dialog.show === 'function') dialog.show();
        } catch (e) { return; }
        /* Trigger image rendering by forcing scroll into view on each li */
        var lis = dialog.querySelectorAll('li');
        Array.prototype.forEach.call(lis, function (li) {
          try { li.scrollIntoView({ behavior: 'instant', block: 'nearest' }); } catch (e) {}
        });
        /* Decode any newly-rendered imgs */
        var imgs = dialog.querySelectorAll('img');
        Array.prototype.forEach.call(imgs, function (img) {
          if (typeof img.decode === 'function') {
            img.decode().catch(function () {});
          }
        });
        /* Close after a moment, restore styles */
        setTimeout(function () {
          try { dialog.close(); } catch (e) {}
          dialog.setAttribute('style', origStyle);
        }, 300);
      }
    );
  }

  /* Patch each <zoom-dialog> open() AND close() to bypass Horizon's view-
     transition wrapper. View-transition adds 50-150ms (snapshot capture +
     callback + finish) even with animation: none. Calling showModal() /
     close() directly is instant. */
  function patchInstantOpen() {
    Array.prototype.forEach.call(
      document.querySelectorAll('zoom-dialog'),
      function (zd) {
        if (zd._ggInstant === '1') return;
        zd._ggInstant = '1';
        var origOpen = zd.open;
        var origClose = zd.close;
        if (typeof origOpen === 'function') {
          zd.open = function (index, event) {
            try {
              if (event && typeof event.preventDefault === 'function') event.preventDefault();
              var refs = this.refs || {};
              var dialog = refs.dialog;
              var media = refs.media || [];
              var thumbnails = refs.thumbnails;
              if (!dialog || !media[index]) return origOpen.call(this, index, event);
              dialog.showModal();
              if (thumbnails && thumbnails.children && thumbnails.children[index]) {
                try { thumbnails.children[index].scrollIntoView({ behavior: 'instant' }); } catch (e) {}
              }
              try { media[index].scrollIntoView({ behavior: 'instant' }); } catch (e) {}
              if (typeof this.selectThumbnail === 'function') {
                try { this.selectThumbnail(index, { behavior: 'instant' }); } catch (e) {}
              }
            } catch (e) { return origOpen.call(this, index, event); }
          };
        }
        if (typeof origClose === 'function') {
          zd.close = function () {
            try {
              if (typeof this.closeDialog === 'function') return this.closeDialog();
              if (this.refs && this.refs.dialog && typeof this.refs.dialog.close === 'function') {
                return this.refs.dialog.close();
              }
            } catch (e) {}
            return origClose.call(this);
          };
        }
      }
    );
  }

  function schedulePreload() {
    /* Run immediately on page load. */
    preloadDialogImages();
    /* Patch zoom-dialog instances ASAP (also re-patches on mutations) */
    patchInstantOpen();
    var mo = new MutationObserver(patchInstantOpen);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    /* On idle, run again + prime the dialog. */
    function deferred() {
      preloadDialogImages();
      patchInstantOpen();
      /* primeDialog() DISABLED — it was scrolling the page to the off-screen
         dialog position via scrollIntoView. The instant-open patch already
         bypasses view transitions, so priming isn't needed. */
    }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferred, { timeout: 2500 });
    } else {
      setTimeout(deferred, 1500);
    }
  }
  if (document.readyState === 'complete') {
    schedulePreload();
  } else {
    window.addEventListener('load', schedulePreload);
  }
  /* Also pre-load when the user hovers the gallery (intent signal).
     Horizon's pointer-enter only fires on the in-dialog thumbnails, not
     on the page-side gallery. We add page-side pointer-enter here. */
  document.addEventListener('mouseenter', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('.product-media-container--zoomable')) {
      preloadDialogImages();
    }
  }, true);
})();

/* OLD STYLE-OVERRIDE BLOCK kept disabled below for reference. */
(function () {
  if (true) return; // Disabled — replaced by injection above
  if (typeof window === 'undefined') return;
  var STYLE = {
    'background': '#FFFDF7',
    'background-color': '#FFFDF7',
    'background-image': 'none',
    'color': '#5C3D2E',
    'border': '1px solid #E8DFD0',
    'border-radius': '50%',
    'width': '44px',
    'height': '44px',
    'position': 'absolute',
    'top': '20px',
    'right': '20px',
    'display': 'inline-flex',
    'align-items': 'center',
    'justify-content': 'center',
    'box-shadow': '0 2px 8px rgba(92, 61, 46, 0.10)',
    'z-index': '100',
    'transition': 'background-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1), color 240ms cubic-bezier(0.22, 0.61, 0.36, 1), border-color 240ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1)'
  };
  function applyStyles(btn) {
    if (!btn) return;
    // Always apply (no once-guard) — Horizon's animations can re-stomp styles
    Object.keys(STYLE).forEach(function (k) { btn.style.setProperty(k, STYLE[k], 'important'); });
    var svg = btn.querySelector('svg');
    if (svg) {
      svg.style.setProperty('color', '#5C3D2E', 'important');
      svg.style.setProperty('width', '16px', 'important');
      svg.style.setProperty('height', '16px', 'important');
      Array.prototype.forEach.call(svg.querySelectorAll('*'), function (n) {
        n.style.setProperty('stroke', 'currentColor', 'important');
        n.style.setProperty('fill', 'currentColor', 'important');
      });
    }
    if (btn.dataset.ggHoverBound !== '1') {
      btn.dataset.ggHoverBound = '1';
      btn.addEventListener('mouseenter', function () {
        btn.style.setProperty('color', '#C9A87C', 'important');
        btn.style.setProperty('border-color', '#C9A87C', 'important');
        btn.style.setProperty('transform', 'translateY(-1px)', 'important');
        var s = btn.querySelector('svg');
        if (s) s.style.setProperty('color', '#C9A87C', 'important');
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.setProperty('color', '#5C3D2E', 'important');
        btn.style.setProperty('border-color', '#E8DFD0', 'important');
        btn.style.setProperty('transform', 'translateY(0)', 'important');
        var s = btn.querySelector('svg');
        if (s) s.style.setProperty('color', '#5C3D2E', 'important');
      });
    }
  }
  function styleAllCloseButtons() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.dialog-zoomed-gallery__close-button'),
      applyStyles
    );
  }
  function bindDialogOpenHook() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.dialog-zoomed-gallery__dialog'),
      function (dialog) {
        if (dialog.dataset.ggOpenBound === '1') return;
        dialog.dataset.ggOpenBound = '1';
        var ao = new MutationObserver(function () {
          // Run multiple times to catch animation frames overwriting our styles
          styleAllCloseButtons();
          setTimeout(styleAllCloseButtons, 50);
          setTimeout(styleAllCloseButtons, 200);
          setTimeout(styleAllCloseButtons, 600);
        });
        ao.observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
      }
    );
  }
  function tick() { styleAllCloseButtons(); bindDialogOpenHook(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick);
  } else {
    tick();
  }
  // Re-style whenever the DOM changes — and also bind any newly-added dialogs.
  var obs = new MutationObserver(tick);
  obs.observe(document.documentElement, { childList: true, subtree: true });
  // Periodic safety net for the first 5 seconds after page load,
  // in case Horizon's animations override our styles late.
  var t = 0;
  var poll = setInterval(function () {
    tick();
    t += 200;
    if (t >= 5000) clearInterval(poll);
  }, 200);
})();

/* ============================================================ */
/* === GG REGION-LOCK: TOGGLE + GLOBAL CART-BYPASS GUARD ====== */
/* ============================================================ */
/* Two responsibilities:
   1. Wire the "Show pieces unavailable in your region" checkbox in the
      recently-arrived section.
   2. Intercept ALL /cart/add.js POST requests sitewide and reject any
      line whose product card carries data-ships-visitor="false". Catches
      hover-ATC, quick-view modal, recently-viewed strip, etc — anywhere
      a user might bypass the PDP region lock.
   Also cleans cart of region-locked items on every PDP load. */
(function () {
  if (typeof window === 'undefined') return;

  // ── 1. Region toggle ─────────────────────────────────────────
  function wireRegionToggles() {
    document.querySelectorAll('[data-region-toggle]').forEach(function (wrap) {
      var cb = wrap.querySelector('[data-region-show-all]');
      var grid = document.querySelector('[data-region-grid]');
      if (!cb || !grid || cb.dataset.ggBound === '1') return;
      cb.dataset.ggBound = '1';
      cb.addEventListener('change', function () {
        grid.setAttribute('data-show-locked', cb.checked ? 'true' : 'false');
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireRegionToggles);
  } else {
    wireRegionToggles();
  }
  new MutationObserver(wireRegionToggles).observe(
    document.documentElement, { childList: true, subtree: true }
  );

  // ── 2. Build a set of region-locked product handles (and variant ids)
  //      that any add-to-cart call should refuse. ──────────────
  function collectLockedHandles() {
    var locked = new Set();
    var lockedVariantIds = new Set();
    document.querySelectorAll('[data-ships-visitor="false"]').forEach(function (el) {
      var handle = el.getAttribute('data-handle') ||
                   el.querySelector('[data-handle]')?.getAttribute('data-handle');
      if (handle) locked.add(handle);
      // Hover-ATC button stores the variant id; collect those too
      el.querySelectorAll('[data-variant-id]').forEach(function (b) {
        var v = b.getAttribute('data-variant-id');
        if (v) lockedVariantIds.add(String(v));
      });
      // Card's href also tells us the product handle
      var href = el.getAttribute('href') || '';
      var m = href.match(/\/products\/([^?#/]+)/);
      if (m && m[1]) locked.add(m[1]);
    });
    // The current PDP itself, if it's locked
    var pdpLock = document.querySelector('.gg-region-lock[data-region-locked="true"]');
    if (pdpLock) {
      var path = location.pathname.match(/\/products\/([^?#/]+)/);
      if (path && path[1]) locked.add(path[1]);
    }
    return { handles: locked, variantIds: lockedVariantIds };
  }

  // ── 3. Intercept fetch + XHR for /cart/add ───────────────────
  function isCartAddUrl(url) {
    if (!url) return false;
    var u = String(url);
    return /\/cart\/add(\.js)?(\?|$)/.test(u);
  }
  function extractIdsFromAddPayload(body) {
    var ids = [];
    try {
      if (body instanceof FormData) {
        // Form-style: id, items[][id]
        for (var pair of body.entries()) {
          var k = pair[0]; var v = pair[1];
          if (k === 'id' || /^items\[\d+\]\[id\]$/.test(k)) ids.push(String(v));
        }
      } else if (typeof body === 'string') {
        try {
          var parsed = JSON.parse(body);
          if (parsed.id) ids.push(String(parsed.id));
          if (Array.isArray(parsed.items)) parsed.items.forEach(function (it) {
            if (it && it.id) ids.push(String(it.id));
          });
        } catch (e) {
          // URL-encoded form body
          new URLSearchParams(body).forEach(function (v, k) {
            if (k === 'id' || /^items\[\d+\]\[id\]$/.test(k)) ids.push(String(v));
          });
        }
      } else if (body && typeof body === 'object') {
        if (body.id) ids.push(String(body.id));
        if (Array.isArray(body.items)) body.items.forEach(function (it) {
          if (it && it.id) ids.push(String(it.id));
        });
      }
    } catch (e) {}
    return ids;
  }
  function showRegionBlockToast() {
    var t = document.createElement('div');
    t.textContent = 'This piece isn’t available in your region — we’ve blocked it from your basket.';
    t.style.cssText =
      'position:fixed;left:50%;bottom:32px;transform:translateX(-50%);' +
      'background:#FFFDF7;color:#5C3D2E;padding:14px 22px;' +
      'border:1px solid #E8DFD0;border-left:3px solid #C9A87C;border-radius:4px;' +
      'box-shadow:0 8px 32px rgba(92,61,46,0.18);font-family:Inter,sans-serif;' +
      'font-size:13px;z-index:99999;max-width:90vw;opacity:0;' +
      'transition:opacity 240ms cubic-bezier(0.22,0.61,0.36,1);';
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.style.opacity = '1'; });
    setTimeout(function () {
      t.style.opacity = '0';
      setTimeout(function () { t.remove(); }, 280);
    }, 3600);
  }

  // Patch fetch
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (isCartAddUrl(url)) {
        var body = (init && init.body) || (typeof input !== 'string' && input.body) || null;
        var ids = extractIdsFromAddPayload(body);
        var locked = collectLockedHandles();
        var blocked = ids.some(function (id) { return locked.variantIds.has(id); });
        if (blocked) {
          showRegionBlockToast();
          return Promise.resolve(new Response(JSON.stringify({
            status: 422,
            description: 'Region-locked product blocked by GlamourGallery storefront.',
            errors: { region: ['This piece is not available in your region.'] }
          }), { status: 422, headers: { 'Content-Type': 'application/json' } }));
        }
      }
      return origFetch.apply(this, arguments);
    };
  }

  // Patch XHR
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._ggUrl = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (isCartAddUrl(this._ggUrl)) {
      var ids = extractIdsFromAddPayload(body);
      var locked = collectLockedHandles();
      var blocked = ids.some(function (id) { return locked.variantIds.has(id); });
      if (blocked) {
        showRegionBlockToast();
        // Fake a 422 response by aborting and dispatching a load with a fake status
        try { this.abort(); } catch (e) {}
        return;
      }
    }
    return origSend.apply(this, arguments);
  };
})();

/* ============================================================ */
/* === GG GLOBAL HOVER-ATC HANDLER (works on ALL sections) === */
/* ============================================================ */
/* Event-delegated [data-rec-atc] handler so card Add buttons work
   site-wide, not just inside the Recently Arrived section's inline JS.
   Fixes the bug where clicking ADD on a Considered Pairings card
   navigates to the PDP instead of adding to cart. */
(function () {
  if (typeof window === 'undefined') return;
  if (window.__ggGlobalAtcBound) return;
  window.__ggGlobalAtcBound = true;

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-rec-atc]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.dataset.ggBusy === '1') return;

    var single  = btn.getAttribute('data-single') === 'true';
    var handle  = btn.getAttribute('data-handle');
    var variant = btn.getAttribute('data-variant-id');

    if (!single) {
      // Multi-variant: open quick-view so customer picks options
      if (window.GG && window.GG.quickView) { window.GG.quickView.open(handle); return; }
      document.dispatchEvent(new CustomEvent('gg:quickview:open', { detail: { handle: handle } }));
      return;
    }
    if (!variant) return;

    btn.dataset.ggBusy = '1';
    var orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      sectionIds.push(el.dataset.sectionId);
    });
    var cartIconEl = document.querySelector('cart-icon[data-section-id], cart-icon');
    if (cartIconEl && cartIconEl.dataset && cartIconEl.dataset.sectionId) {
      sectionIds.push(cartIconEl.dataset.sectionId);
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({
        items:    [{ id: Number(variant), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { return Promise.reject(j); });
        return r.json();
      })
      .then(function (response) {
        btn.textContent = 'Added ✓';
        fetch('/cart.js').then(function (cr) { return cr.json(); }).then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'gg-hover-atc',
              data:     { sections: response.sections, source: 'gg-hover-atc' },
            },
          }));
        });
        // Fly-to-cart animation
        try {
          var card = btn.closest('.rec-card');
          var img  = card && card.querySelector('.rec-card__img');
          var dest = document.querySelector('a.header-actions__action--cart')
                  || document.querySelector('cart-icon')
                  || document.querySelector('[data-testid="cart-drawer-trigger"]');
          if (img && dest && img.src) {
            var sR = img.getBoundingClientRect();
            var dR = dest.getBoundingClientRect();
            var ghost = document.createElement('img');
            ghost.src = img.src;
            ghost.style.cssText =
              'position:fixed;z-index:99999;pointer-events:none;' +
              'left:' + sR.left + 'px;top:' + sR.top + 'px;' +
              'width:' + sR.width + 'px;height:' + sR.height + 'px;' +
              'border-radius:4px;object-fit:cover;' +
              'transition:transform 700ms cubic-bezier(0.45,0.05,0.55,0.95),opacity 700ms ease;' +
              'box-shadow:0 8px 24px rgba(43,31,24,0.18);';
            document.body.appendChild(ghost);
            ghost.getBoundingClientRect();
            var dx = (dR.left + dR.width/2) - (sR.left + sR.width/2);
            var dy = (dR.top  + dR.height/2) - (sR.top  + sR.height/2);
            ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.15)';
            ghost.style.opacity = '0';
            setTimeout(function () { ghost.remove(); }, 720);
          }
        } catch (e) {}
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = orig;
          delete btn.dataset.ggBusy;
        }, 1400);
      })
      .catch(function (err) {
        // Region-lock guard kicks in here (returns 422). Reset gracefully.
        btn.textContent = orig;
        btn.disabled = false;
        delete btn.dataset.ggBusy;
      });
  }, false);

  // Wishlist heart — also delegate site-wide
  document.addEventListener('click', function (e) {
    var fav = e.target.closest('.rec-card__fav, [data-fav-btn]');
    if (!fav) return;
    e.preventDefault();
    e.stopPropagation();
    var productId = fav.getAttribute('data-product-id');
    var wasPressed = fav.getAttribute('aria-pressed') === 'true';
    var next = !wasPressed;
    fav.setAttribute('aria-pressed', String(next));
    if (window.GG && window.GG.wishlist) { window.GG.wishlist.toggle(productId); return; }
    document.dispatchEvent(new CustomEvent('gg:wishlist:toggle', {
      detail: { productId: productId, saved: next }
    }));
  }, false);
})();

/* ============================================================ */
/* === GG UNIVERSAL REGION-LOCK CARD HIDER ==================== */
/* ============================================================ */
/* Hides region-locked product cards in ANY container — search modal,
   predictive search, recommendations, recently-viewed widgets, etc.
   Works by URL: any <a href="/products/<handle>"> whose handle is
   in the region-lock cache gets its containing card hidden. */
(function () {
  if (typeof window === 'undefined') return;
  if (window.__ggRegionHider) return;
  window.__ggRegionHider = true;

  var CACHE_KEY = 'gg_region_lock_cache';
  function loadCache() {
    try { var c = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); return c && typeof c === 'object' ? c : {}; }
    catch (e) { return {}; }
  }
  function saveCache(c) { try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) {} }

  function handleFromHref(href) {
    if (!href) return null;
    var m = String(href).match(/\/products\/([^?#/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* Walk up from the product link to the visual card container.
     Strategy in priority order:
       1. [data-product-handle] — our custom .gg-card uses this attribute,
          and it identifies the card uniquely regardless of grid structure.
          Used by the custom collection grid (.gg-product-grid > article).
       2. Find nearest known grid CONTAINER, then return the direct child
          of that container that the link sits within. Handles cases where
          the card has multiple <li> ancestors (variant swatch list inside
          the card etc.) — the inner ones are skipped because they're
          descendants of the cell, not direct children of the grid.
       3. Class-based fallback for any straggler layouts. */
  function findCardElement(link) {
    var byHandle = link.closest('[data-product-handle], .gg-card, .product-grid__item, .resource-list__item, .collection-list__item, .product-list__item');
    if (byHandle) return byHandle;

    var GRID_SELECTORS = [
      '.gg-product-grid',                           // GlamourGallery custom collection grid (.gg-card inside)
      '.gg-coll-products',                          // outer wrapper
      '.product-grid',                              // Horizon main collection
      'ul.product-grid',
      '.resource-list',
      '.collection-list',
      '.product-list',
      '[data-region-grid]',                         // Recently Arrived
      'overflow-list',                              // search modal etc.
      '.predictive-search__products',
      '.gg-grid',
    ].join(', ');
    var grid = link.closest(GRID_SELECTORS);
    if (grid) {
      var el = link;
      while (el && el.parentElement && el.parentElement !== grid) {
        el = el.parentElement;
      }
      if (el && el !== link) return el;
    }
    var li = link.closest('li');
    if (li) return li;
    var probe = link;
    for (var i = 0; i < 6; i++) {
      if (!probe || !probe.parentElement) break;
      var cls = (probe.className || '') + '';
      if (/(card|tile|item)(\b|_|-)/i.test(cls) && probe !== link) return probe;
      probe = probe.parentElement;
    }
    return link;
  }

  function hideIfLocked(link, cache, pending) {
    if (!link || link.dataset.ggRegionChecked === '1') return;
    var handle = handleFromHref(link.getAttribute('href'));
    if (!handle) return;

    /* Skip the main PDP link on the current page — we don't want to
       hide breadcrumbs / "you are here" links. */
    var current = (location.pathname.match(/\/products\/([^?#/]+)/) || [])[1];
    if (handle === current) return;

    /* Skip nav menu items (links inside header/nav containers) */
    if (link.closest('header, nav, .header, .nav, .menu, [role="navigation"]')) return;

    /* Skip the Recently Arrived show-locked grid — that's the one
       opt-in place where locked cards are explicitly visible. */
    if (link.closest('[data-region-grid][data-show-locked="true"]')) return;

    link.dataset.ggRegionChecked = '1';

    if (cache.hasOwnProperty(handle)) {
      if (cache[handle]) {
        var card = findCardElement(link);
        if (card) card.style.display = 'none';
      }
      return;
    }
    /* Not cached — fetch lazily. Capped at 8 concurrent fetches so we
       don't slam the origin if there are 30+ unknown cards on screen. */
    if (pending.count >= 8) return;
    pending.count++;
    fetch('/products/' + encodeURIComponent(handle), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var locked = /class="[^"]*gg-region-lock[^"]*"[^>]*data-region-locked="true"/.test(html);
        var c = loadCache();
        c[handle] = locked;
        saveCache(c);
        if (locked) {
          var card = findCardElement(link);
          if (card) card.style.display = 'none';
        }
      })
      .catch(function () {})
      .finally(function () { pending.count--; });
  }

  function sweep(root) {
    var cache = loadCache();
    var pending = { count: 0 };
    var scope = root || document;
    var links = scope.querySelectorAll('a[href*="/products/"]:not([data-gg-region-checked])');
    Array.prototype.forEach.call(links, function (l) { hideIfLocked(l, cache, pending); });
  }

  /* Run once on load + observe future DOM mutations (search modal opening,
     predictive-search results, etc.). */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sweep(); });
  } else {
    sweep();
  }
  var debounceTimer = null;
  var mo = new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { sweep(); }, 120);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================ */
/* === GG VARIANT PICKER SWATCH + LABEL ENHANCER ============== */
/* ============================================================ */
/* Parses each variant pill's text, extracts the meaningful colour
   word, prepends a coloured swatch dot, and replaces the long
   label with the clean short form. */
(function () {
  if (typeof window === 'undefined') return;
  if (window.__ggSwatchEnhanced) return;
  window.__ggSwatchEnhanced = true;

  // Colour name → hex map. Keys lowercase, matched as substrings.
  var SWATCH_MAP = {
    'walnut':       '#5C3D2E',
    'natural':      '#D8C9A3',
    'oak':          '#C19A6B',
    'teak':         '#9A6B3F',
    'bamboo':       '#D6BF8B',
    'rattan':       '#C9A87C',
    'wicker':       '#B89968',
    'pine':         '#E8D2A8',
    'ash':          '#D2C4A8',
    'birch':        '#E8DEC4',
    'beech':        '#D9B98C',
    'cream':        '#F5F0E8',
    'ivory':        '#FFFDF7',
    'off-white':    '#F8F4EC',
    'off white':    '#F8F4EC',
    'white':        '#FFFFFF',
    'beige':        '#E8DFD0',
    'stone':        '#D7CDBE',
    'sand':         '#E0D2B5',
    'linen':        '#EDE3D1',
    'taupe':        '#B8A793',
    'camel':        '#C19A6B',
    'brass':        '#C9A87C',
    'gold':         '#D4B570',
    'terracotta':   '#B96E3A',
    'clay':         '#B47457',
    'sage':         '#8A9E85',
    'olive':        '#7C7A4D',
    'green':        '#8A9E85',
    'black':        '#1C1C1C',
    'charcoal':     '#3A3735',
    'grey':         '#9C9692',
    'gray':         '#9C9692',
    'nude':         '#E5C7A7',
    'oat':          '#E2D5BC'
  };

  function extractColour(text) {
    if (!text) return { hex: null, label: '', matched: false };
    var lower = String(text).toLowerCase();
    /* Strip noise prefixes like "bulb not included" so we can find the actual colour */
    var cleaned = lower
      .replace(/bulb\s*(not\s*)?included/g, '')
      .replace(/colour|color/g, '')
      .replace(/\bstyle\s+[a-z0-9]+\b/g, '')   /* drop "Style H" etc. */
      .trim();

    /* Prefer multi-word matches first (off-white before white, etc.) */
    var keys = Object.keys(SWATCH_MAP).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      if (cleaned.indexOf(keys[i]) !== -1) {
        var label = keys[i].replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        return { hex: SWATCH_MAP[keys[i]], label: label, matched: true };
      }
    }
    /* No colour match (e.g. size option, dimension, "Standard"). Return the
       original short text and skip the dot — chips/pills still render as
       clean text-only buttons. */
    return { hex: null, label: String(text).trim().slice(0, 32), matched: false };
  }

  function enhance(label) {
    if (!label || label.dataset.ggSwatched === '1') return;
    label.dataset.ggSwatched = '1';
    var textSpan = label.querySelector('.variant-option__button-label__text')
                || label.querySelector('[data-key="variant-option-text"]');
    var rawText = (textSpan ? textSpan.textContent : label.textContent) || '';
    rawText = rawText.replace(/\s+/g, ' ').trim();
    if (!rawText) return;

    var info = extractColour(rawText);

    // Tooltip = full original variant title
    label.setAttribute('title', rawText);

    // Replace the text span content with the cleaned short label
    if (textSpan) {
      textSpan.textContent = info.label || rawText.slice(0, 32);
    }

    // Only inject a swatch dot if we matched a real colour. Size options
    // (Small, 30cm, Large, etc.) render as clean text-only pills.
    if (info.matched && info.hex) {
      var dot = document.createElement('span');
      dot.className = 'gg-swatch-dot';
      dot.style.backgroundColor = info.hex;
      label.insertBefore(dot, label.firstChild);
    }
  }

  function sweep() {
    document.querySelectorAll('.variant-option--buttons:not(.variant-option--swatches) label.variant-option__button-label').forEach(enhance);
    sweepSwatches();
  }

  /* SWATCH MODE — replace Horizon's variant-image swatch background
     with a solid brand colour pulled from the option name. The product
     photo inside each swatch was reading as a circle-in-a-circle. */
  function getOptionName(label) {
    var input = label.querySelector('input[type="radio"]');
    if (input) {
      return input.getAttribute('aria-label')
          || input.getAttribute('value')
          || '';
    }
    return label.getAttribute('aria-label') || label.textContent || '';
  }

  function sweepSwatches() {
    var labels = document.querySelectorAll('.variant-option--swatches label.variant-option__button-label--has-swatch');
    labels.forEach(function (label) {
      if (label.dataset.ggSwatchPainted === '1') return;
      var swatch = label.querySelector('.swatch');
      if (!swatch) return;
      var name = getOptionName(label);
      if (!name) return;
      var info = extractColour(name);
      if (!info.matched || !info.hex) return;
      label.dataset.ggSwatchPainted = '1';
      label.setAttribute('title', String(name).trim());
      /* Hard override: solid colour, no image */
      swatch.style.setProperty('background', info.hex, 'important');
      swatch.style.setProperty('background-image', 'none', 'important');
      swatch.style.setProperty('background-color', info.hex, 'important');
      swatch.classList.remove('swatch--variant-image');
    });
  }

  /* Expose extractColour so Quick View (and any future component) can render
     the same swatch-dot + cleaned-label treatment without duplicating the map. */
  window.ggExtractColour = extractColour;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
  var mo = new MutationObserver(sweep);
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================ */
/* === PDP GALLERY VARIANT FILTER ============================= */
/* ============================================================ */
/* Horizon's default gallery shows every product image regardless of which
   variant the customer has selected. Shopify exposes image.variant_ids
   per image (set by the owner via the Tag photos modal) but the stock
   theme ignores it. This module:

   1. Fetches /products/<handle>.js on PDP load
   2. Builds a media_id → variant_ids[] map
   3. Listens for variant change (radio input change inside variant-picker)
   4. Hides gallery <li>s whose media has variant_ids set but doesn't
      contain the currently-selected variant id. Untagged photos (empty
      variant_ids) stay visible for every variant.
*/
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ggGalleryVariantFilter) return;
  window.__ggGalleryVariantFilter = true;

  /* Run only on product pages. Horizon's PDP URL contains /products/<handle> */
  var handleMatch = location.pathname.match(/\/products\/([^/?#]+)/);
  if (!handleMatch) return;
  var handle = handleMatch[1];

  /* Cache: media.id → variant_ids[] (numbers) */
  var mediaVariantMap = null;

  function selectedVariantId() {
    /* 1. Shopify Markets puts ?variant=ID in URL when a variant is selected */
    var urlVid = new URLSearchParams(location.search).get('variant');
    if (urlVid) return Number(urlVid);
    /* 2. Active variant picker radio (Horizon updates this on change) */
    var checked = document.querySelector('variant-picker input[type="radio"][data-current-checked="true"]')
              || document.querySelector('variant-picker input[type="radio"]:checked');
    if (checked && checked.dataset.variantId) return Number(checked.dataset.variantId);
    return null;
  }

  function galleryMediaItems() {
    /* Every Horizon media element gets data-media-id, both in the main
       slideshow (.product-media inside slideshow-slide) and in the zoom
       dialog (li[id^="product-"]). Catch them all. */
    return document.querySelectorAll('[data-media-id]');
  }

  function mediaIdFromEl(el) {
    var raw = el.getAttribute && el.getAttribute('data-media-id');
    return raw ? Number(raw) : null;
  }

  /* Walk up to the right "hideable" container for this media:
       - slideshow-slide (main carousel)
       - li[id^="product-"] (zoom dialog gallery list)
       - .product-media-container (grid/two-column layouts)
       Falls back to the element itself if none found. */
  function findHideAncestor(el) {
    return el.closest('slideshow-slide')
        || el.closest('li[id^="product-"]')
        || el.closest('.product-media-container')
        || el;
  }

  function apply(selectedVid) {
    if (!mediaVariantMap || !selectedVid) return;
    var items = galleryMediaItems();
    if (!items.length) return;
    items.forEach(function (el) {
      var mediaId = mediaIdFromEl(el);
      if (!mediaId) return;
      var vids = mediaVariantMap.get(mediaId);
      var target = findHideAncestor(el);
      /* No variant_ids on this media → "shown for all". Always visible. */
      if (!vids || !vids.length) {
        target.style.removeProperty('display');
        target.removeAttribute('data-gg-variant-hidden');
        target.removeAttribute('aria-hidden');
        return;
      }
      if (vids.indexOf(selectedVid) !== -1) {
        target.style.removeProperty('display');
        target.removeAttribute('data-gg-variant-hidden');
        target.removeAttribute('aria-hidden');
      } else {
        target.style.display = 'none';
        target.setAttribute('data-gg-variant-hidden', '1');
        target.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function bindVariantChange() {
    /* Re-apply whenever a variant radio changes or the URL variant param
       updates (Shopify can do this without a navigation). */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.type !== 'radio') return;
      if (!t.closest('variant-picker, .variant-picker, swatches-variant-picker-component')) return;
      /* Defer one tick — Horizon updates data-current-checked + the URL
         after its own listeners fire. */
      setTimeout(function () { apply(selectedVariantId()); }, 30);
    });
    /* History changes (variant param updates) */
    window.addEventListener('popstate', function () {
      apply(selectedVariantId());
    });
  }

  function loadProductJSON() {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (!product || !Array.isArray(product.media)) return;
        mediaVariantMap = new Map();
        product.media.forEach(function (m) {
          var vids = Array.isArray(m.variant_ids) ? m.variant_ids.map(Number) : [];
          mediaVariantMap.set(Number(m.id), vids);
        });
        apply(selectedVariantId());
      })
      .catch(function () { /* non-fatal — gallery stays unfiltered */ });
  }

  function init() {
    if (!document.querySelector('variant-picker, .variant-picker, swatches-variant-picker-component')) return;
    loadProductJSON();
    bindVariantChange();
    /* Re-apply when Horizon swaps the gallery DOM (e.g. on variant change
       it re-renders sorted_media). Throttled to avoid runaway. */
    var apTimer = null;
    var mo = new MutationObserver(function () {
      clearTimeout(apTimer);
      apTimer = setTimeout(function () { apply(selectedVariantId()); }, 80);
    });
    var gallery = document.querySelector('.product-media-gallery, [class*="product-media"]')?.parentElement || document.body;
    mo.observe(gallery, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
